import { useCallback, useEffect, useState } from 'react';
import type { OperacionesClienteFinal } from '../api/ops/clienteFinal';
import { partesDelQr, type PasePublico } from '../api/ops/pasesCliente';
import { ErrorApi } from '../api/problema';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { leerConfiguracion } from '../config';
import { navegador } from '../logica/navegador';
import { RUTAS_CLIENTE } from '../rutas';
import { guardarImagenQr, useCarteras, type Nota } from './carteras';
import { BloqueQr, BotonesCartera, NotaPase } from './PaseComun';
import { BotonPc, Pagina } from './Pagina';
import { chipDelPase, cuando, fechaAnterior, paseNDeN, REENVIO_SIN_CONTACTO, sePuedeUsar, textoReenviado, ventana } from './pases';

/**
 * La página del pase (6.6): la dirección que lleva el QR (`{pases.url_pase}/v1.<negocio>.<código>.<kid>.<firma>`), anónima,
 * con `getPublicPass`. QR grande con su código y «Ampliar», «Pase n de N» (G414, G785), la fecha cambiada con el antes y el
 * ahora (mismo QR, G17), usado, anulado y caducado; las carteras (6.8, G50; Apple sin `401`, G422), «Reenviármelo» al
 * contacto que ya tiene (`resendPublicPass`, 3 por hora), «Guardar el QR» (G602) y «Ver todos mis pases». La marca, el
 * logo de Negocios (`brand`, G785; solo `https` del almacén de Reservaria, G815). El color de marca (`brand.primary_color`,
 * G803) lo usan las carteras (6.8) y el avatar de la cabecera (G978); sin él, el rojo de Reservaria. El resto de la página
 * lleva los colores de Reservaria.
 */
interface Props {
    ops: OperacionesClienteFinal;
    /** `PANEL_URL` (`VITE_PANEL_URL`); vacía, el mismo origen. */
    urlPanel?: string;
    /** La última parte de la dirección: `v1.<negocio>.<código>.<kid>.<firma>`. */
    direccion: string;
    navegar: (ruta: string) => void;
}

type Carga = { tipo: 'cargando' } | { tipo: 'error' } | { tipo: 'no-valido'; mal: boolean } | { tipo: 'listo'; pase: PasePublico };

export function PaginaPase({ ops, direccion, navegar, urlPanel = leerConfiguracion().panel }: Props) {
    // G891: esta página se sirve en `pase.reservaria.app`, que solo da la página del pase (G821): «Ver mis pases» va a
    // `PANEL_URL/mis-pases` con una carga nueva; en el mismo origen que el panel, sin recargar.
    const verMisPases = () => {
        if (urlPanel !== '' && urlPanel !== window.location.origin) {
            navegador.ir(`${urlPanel}${RUTAS_CLIENTE.misPases}`);
            return;
        }
        navegar(RUTAS_CLIENTE.misPases);
    };
    const partes = partesDelQr(direccion);
    const [carga, setCarga] = useState<Carga>(partes === null ? { tipo: 'no-valido', mal: true } : { tipo: 'cargando' });
    const [intento, setIntento] = useState(0);
    const [nota, setNota] = useState<Nota | null>(null);
    const [reenviando, setReenviando] = useState(false);
    const [agotado, setAgotado] = useState(false);
    const { ocupada, anadir } = useCarteras(ops, setNota);
    const negocio = partes?.negocio ?? '';
    const token = partes?.token ?? '';

    useEffect(() => {
        if (negocio === '') {
            return;
        }
        let vivo = true;
        ops.getPublicPass(negocio, token)
            .then((pase) => {
                if (vivo) {
                    setCarga({ tipo: 'listo', pase });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCarga(e instanceof ErrorApi && (e.estado === 404 || e.estado === 400) ? { tipo: 'no-valido', mal: e.estado === 400 } : { tipo: 'error' });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, token, intento]);

    const reenviar = useCallback(() => {
        setReenviando(true);
        setNota(null);
        ops.resendPublicPass(negocio, token)
            .then((r) => {
                setAgotado(r.remaining_this_hour === 0);
                setNota(['circle-check', 'info', textoReenviado(r.channel, r.destination_hint, r.remaining_this_hour)]);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 429) {
                    setAgotado(true);
                    setNota(['clock', 'espera', 'Ya te lo hemos mandado 3 veces en la última hora. Prueba más tarde o guárdalo en la cartera del móvil.']);
                } else if (e instanceof ErrorApi && e.estado === 409) {
                    // G1080: sin contacto (`no_contact`) o pase anulado o caducado (`conflict`).
                    setNota(e.codigo === 'no_contact' ? ['user-x', 'error', REENVIO_SIN_CONTACTO] : ['ban', 'error', 'No hemos podido reenviar este pase: ya no vale.']);
                } else {
                    setNota(['circle-alert', 'error', 'No hemos podido reenviarlo. Comprueba la conexión y prueba de nuevo.']);
                }
            })
            .finally(() => {
                setReenviando(false);
            });
    }, [ops, negocio, token]);

    if (carga.tipo === 'cargando') {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'reservaria' }}>
                <section className="rv-pc-card" aria-busy="true" aria-label="Cargando tu pase">
                    <div className="rv-pc-esqueleto" style={{ height: 24, width: '50%' }} />
                    <div className="rv-pc-esqueleto" style={{ height: 32, width: '80%' }} />
                    <div className="rv-pc-esqueleto" style={{ height: 248, width: 248, alignSelf: 'center' }} />
                </section>
            </Pagina>
        );
    }
    if (carga.tipo === 'error') {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'reservaria' }} centrada>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>No hemos podido cargar tu pase</h1><span>Comprueba la conexión y prueba de nuevo.</span></div>
                    <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setIntento((i) => i + 1); }} />
                </section>
            </Pagina>
        );
    }
    if (carga.tipo === 'no-valido') {
        return (
            <Pagina cabecera={{ tipo: 'cuenta' }} pie={{ tipo: 'reservaria' }} centrada>
                <section className="rv-pc-card" aria-labelledby="rv-ps-nv-t">
                    <div className="rv-pc-baldosa-fila">
                        <span aria-hidden="true" className="rv-pc-baldosa" style={{ background: 'var(--rv-paper)', color: 'var(--rv-ink)' }}><Icono nombre={carga.mal ? 'link' : 'search'} /></span>
                        <Chip estado="problem">Enlace no válido</Chip>
                    </div>
                    <div className="rv-pc-card__cab">
                        <h1 id="rv-ps-nv-t">{carga.mal ? 'El enlace no es válido' : 'No encontramos este pase'}</h1>
                        <span>Puede que el enlace esté incompleto. Ábrelo otra vez desde el email o el mensaje que te mandamos, o entra en «Mis pases».</span>
                    </div>
                    <BotonPc texto="Ver mis pases" icono="ticket" onClick={verMisPases} />
                </section>
            </Pagina>
        );
    }

    const p = carga.pase;
    const f = cuando(p);
    const antes = fechaAnterior(p);
    const cambiada = antes !== null && p.status === 'active';
    const chip = chipDelPase(p.status, cambiada);
    const usable = sePuedeUsar(p.status);
    const usado = p.status === 'inside';
    const nDeN = paseNDeN(p);
    const tipo = p.label ?? null;
    const ceja = [nDeN, tipo].filter((x): x is string => x !== null && x !== '').join(' · ');
    const estado: [NombreIcono, string, string, string, string] | null =
        p.status === 'inside'
            ? ['door-open', 'var(--rv-paper)', 'var(--rv-ink)', 'Ya se ha usado para entrar', 'Cada pase vale para entrar una vez.']
            : p.status === 'voided'
              ? ['ban', 'var(--rv-red-soft)', 'var(--rv-red)', 'Este pase está anulado', `Ya no vale para entrar. Si se ha cancelado, ${p.business_name} te ha escrito con lo que pasa con tu dinero.`]
              : p.status === 'expired'
                ? ['hourglass', 'var(--rv-paper)', 'var(--rv-ink)', 'Este pase ya no vale', `Era para el ${f.largo.charAt(0).toLowerCase()}${f.largo.slice(1)} y no se usó.`]
                : null;
    const filas: [NombreIcono, string, string][] = [
        ['calendar', 'Cuándo', `${f.largo} · ${f.hora}`],
        ['clock', 'Puedes entrar', ventana(p)],
        ...(tipo === null ? [] : [['ticket', 'Tipo', tipo] as [NombreIcono, string, string]]),
    ];
    const nombre = p.holder_name ?? '';

    return (
        <Pagina cabecera={{ tipo: 'negocio', nombre: p.business_name, linea: 'Tu pase', logo: p.brand?.logo_url ?? null, color: p.brand?.primary_color ?? null }} pie={{ tipo: 'reservaria' }}>
            {cambiada && (
                <div className="rv-ps-cambio" role="status">
                    <Icono nombre="calendar-clock" />
                    <span>
                        <strong>Ha cambiado la fecha</strong>
                        <span>{`Antes: ${antes}. Ahora: ${f.corto} · ${f.hora}.`}</span>
                        <span>Tu QR sigue valiendo. Si lo guardaste en la cartera del móvil, se actualiza solo.</span>
                    </span>
                </div>
            )}
            <section className="rv-pc-card rv-ps-pase" aria-labelledby="rv-ps-titulo">
                <div className="rv-ps-pase__cab">
                    {ceja !== '' && <span className="rv-pc-ceja">{ceja}</span>}
                    {chip !== null && <Chip estado={chip[1]}>{chip[0]}</Chip>}
                </div>
                <h1 id="rv-ps-titulo" className="rv-ps-pase__titulo">{p.title}</h1>
                <span className="rv-ps-pase__cuando">{`${f.corto} · ${f.hora}`}</span>
                {nombre !== '' && (
                    <div className="rv-ps-pase__nombre">
                        <span>A nombre de</span>
                        <strong>{nombre}</strong>
                    </div>
                )}
                {estado !== null && (
                    <div className="rv-ps-estado" style={{ background: estado[1] }}>
                        <Icono nombre={estado[0]} style={{ color: estado[2] }} />
                        <span><strong>{estado[3]}</strong><span>{estado[4]}</span></span>
                    </div>
                )}
                {usable || usado ? (
                    <BloqueQr qr={p.qr_url} codigo={p.code ?? ''} etiqueta={`Código QR del pase${nombre === '' ? '' : ` de ${nombre}`}`} usado={usado} />
                ) : (
                    <div className="rv-ps-sinqr">
                        <Icono nombre={p.status === 'voided' ? 'ban' : 'hourglass'} />
                        <strong>{p.status === 'voided' ? 'QR anulado' : 'QR caducado'}</strong>
                        {p.code !== undefined && <s>{p.code}</s>}
                    </div>
                )}
                {usable && (
                    <BotonesCartera
                        apple={p.apple_wallet_url != null}
                        google={p.google_wallet_url != null}
                        ocupada={ocupada}
                        onApple={() => { setNota(null); anadir('apple', negocio, token); }}
                        onGoogle={() => { setNota(null); anadir('google', negocio, token); }}
                    />
                )}
                <div className="rv-ps-detalles">
                    <strong>Detalles</strong>
                    {filas.map(([icono, k, v]) => (
                        <div key={k} className="rv-ps-detalle"><Icono nombre={icono} /><span>{k}</span><strong>{v}</strong></div>
                    ))}
                </div>
                {usable && (
                    <div className="rv-pc-acciones">
                        {p.can_resend === true && <BotonPc texto="Reenviármelo" icono="send" tipo="sec" ocupado={reenviando} ocupadoTexto="Enviando…" desactivado={agotado} onClick={reenviar} />}
                        <BotonPc texto="Guardar el QR" icono="download" tipo="sec" onClick={() => {
                            guardarImagenQr(p.qr_url, `pase-${p.code ?? 'qr'}`);
                            setNota(['check', 'info', 'Imagen del QR guardada. Enséñala en la puerta si no tienes conexión.']);
                        }} />
                    </div>
                )}
                <NotaPase nota={nota} />
            </section>
            <button type="button" className="rv-pc-l rv-pc-l--centro rv-final-osc rv-ps-mios" onClick={verMisPases}>
                <Icono nombre="ticket" />
                {`Ver todos mis pases en ${p.business_name}`}
            </button>
        </Pagina>
    );
}
